import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';
import { useTranslation } from "react-i18next";

import { updateHomepageFooter } from '../../../../../../store/actions/admin/create-event';
import { EventsState } from '../../../../../../store/types';
import { LanguagesAbbr, PageModuleType } from '../../../../../../types/working-model';
import { UploadImageModal } from "../../../../../general-ui/image-upload-modal/image-upload-modal";
import Icon, { COLORS, ICONS } from '../../../../../general-ui/icon';
import Controls, { InitialControlsDisplay } from '../../../session/editor/moduleControls/controls';
import NavItem, { checkLinkedSectionIsOn, filterActiveModules } from './../header/nav-item';
import { TemplateClassNames } from '../../../../../../types/template-layouts';
import LogoBrandlive from '../../../../../../images/BrandLive_Logo.svg';
import LogoBrandliveLight from '../../../../../../images/BrandLive_Logo_light.svg';
import { getHeaderStylingOverrides } from '../../../../../live-event/utils';
import '../../../../../live-event/marketing-page/footer/footer.scss';
import './footer.scss';
import EditSectionColorsModal, { EContentColorOverrideNames } from '../../../session/editor/moduleControls/edit-section-colors-modal';

interface IEditorFooterProps {
	handleScroll: (p: PageModuleType) => void;
	template: TemplateClassNames;
	baseLanguage: LanguagesAbbr;
	language: LanguagesAbbr
}

const EditorFooter: React.FC<IEditorFooterProps> = ({ handleScroll, template, baseLanguage, language }) => {
	const {
		CreateEventReducer: { workingEvent },
	} = useSelector((event: EventsState) => event);
	const [logo, setLogo] = useState<string | null | undefined>(workingEvent?.homepage?.footer?.logo);
	const [openEditLogoModal, setOpenEditLogoModal] = useState(false);
	const [openEditNavItemColor, setOpenEditNavItemColor] = useState(false);
	const [activePageModules, setActivePageModules] = useState(filterActiveModules(workingEvent?.homepage?.modules));

	const dispatch = useDispatch();

	useEffect(() => {
		setActivePageModules(filterActiveModules(workingEvent?.homepage?.modules));
	}, [workingEvent?.homepage?.modules]);

	const { t } = useTranslation("homepage");

	const saveLogo = useCallback((image: { original: string, edited: string; }) => {
		// TODO: refactor to keep original image for future editing
		dispatch(
			updateHomepageFooter({ ...workingEvent!.homepage!.footer, logo: image.edited })
		);
		setLogo(image.edited);
	}, [dispatch, workingEvent]);

	function setNavItemColorOverrides(colorKey: string) {
		if (!workingEvent?.homepage || !workingEvent?.homepage?.footer) return;
		const workingStylingOverrides = { ...workingEvent?.homepage.footer?.styling_overrides };

		const isRestoringDefault = colorKey === "-color";

		// when implementing the callToAction overrides, make sure to include the navItems in the default
		if (!workingStylingOverrides?.content) {
			workingStylingOverrides.content = {
				navItems: { color: isRestoringDefault ? "" : colorKey },
				callToAction: { backgroundColor: '', borderColor: '', text: { color: '' } }
			};
		} else {
			workingStylingOverrides.content.navItems = { color: isRestoringDefault ? "" : colorKey };
		}

		if (workingStylingOverrides && colorKey) {
			dispatch(
				updateHomepageFooter({
					...workingEvent.homepage.footer,
					styling_overrides: workingStylingOverrides
				})
			);
		}
	}

	const footerControllerDisplay: InitialControlsDisplay = {
		editLayout: true,
		editColors: true,
	};

	const navItems = workingEvent?.homepage?.footer?.navItems;
	const {
		backgroundColor,
		backgroundImg,
		mainTextOverrides
	} = getHeaderStylingOverrides(workingEvent?.homepage?.footer?.styling_overrides);

	return workingEvent?.homepage ? (
		<div className={classNames('editor-wrapper footer marketing-page-footer', template, backgroundColor)}>
			<Controls
				display={footerControllerDisplay}
				openEditModal={() => 'leave blank'}
				module={workingEvent.homepage.footer}
				baseLanguage={baseLanguage}
				language={language}
			/>
			<div className={classNames("footer-container")} style={backgroundImg}>
				<div className="footer-left">
					{logo ? (
						<img
							className="footer-logo"
							alt="Logo"
							onClick={() => setOpenEditLogoModal(true)}
							src={logo}
						/>
					) : (
						<h3
							onClick={() => setOpenEditLogoModal(true)}
						>
							{t("Your Logo")}
						</h3>
					)}
					<button onClick={() => setOpenEditLogoModal(true)} className="round edit-button">
						<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
					</button>
				</div>
				<nav className="footer-nav">
					<div className="nav-item-container">
						{navItems && navItems.map(navItem => {
							if (checkLinkedSectionIsOn(navItem, activePageModules)) {
								// get the overrideColor to reuse for the hover class
								const overrideColor = mainTextOverrides?.split('-')[0];
								return (
									<NavItem
										navItem={navItem}
										key={navItem.button_name.base}
										handleScroll={handleScroll}
										className={classNames("footer-nav-item", mainTextOverrides, { [`${overrideColor}-hover`]: mainTextOverrides })}
										isEditor={true}
									/>
								);
							}

						})}
						<button onClick={() => setOpenEditNavItemColor(true)} className="round edit-button">
							<Icon name={ICONS.EDIT} color={COLORS.WHITE} size={12} />
						</button>
					</div>
				</nav>
				<div className="footer-right">
					<div className="footer-author">
						<div className="footer-author-text">POWERED BY</div>
						<img src={template === TemplateClassNames['Studio 54'] ? LogoBrandliveLight : LogoBrandlive} aria-hidden="true" alt="powered by Brandlive" />
					</div>
				</div>
				{/* remove copyright pending further discussions - keep div for formating */}
				<div className="footer-copyright">
					{/* <p>&copy; {new Date().getFullYear()}. All rights reserved. Created by Brandlive.com</p> */}
				</div>
			</div>
			<UploadImageModal
				originalImage={logo}
				fileTitle={"Logo"}
				open={openEditLogoModal}
				close={() => setOpenEditLogoModal(false)}
				onSave={saveLogo}
			/>
			<EditSectionColorsModal
				open={openEditNavItemColor}
				onClose={() => setOpenEditNavItemColor(false)}
				onFinish={setNavItemColorOverrides}
				title={EContentColorOverrideNames.navItems}
			/>
		</div>
	) : (
		<div></div>
	);
};

export default EditorFooter;